import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  ConversationPreview,
  EmptyState,
  FilterChips,
  StatusDot,
  Text,
  useToast,
  type ConversationEntry,
  type FilterChipItem,
} from '@teals/ui-web';
import {
  useCloneSessionMutation,
  useGetSessionHistoryQuery,
  useGetSessionsQuery,
  useGetAnalyticsSessionQuery,
  type SessionListItem,
} from '../../store/api';
import {
  approxDuration,
  deriveSource,
  deriveStatus,
  formatSessionId,
  provisionalSummary,
  relativeTime,
  sourceLabel,
  statusLabel,
  withinTimeRange,
  type SessionStatus,
} from './sessionHelpers';

const PAGE_SIZE = 10;

export interface SessionsTabProps {
  agentId: string;
  agentName: string;
  onNewSession: () => void;
  onOpenInPlayground: (sessionId: string) => void;
}

type FilterId = 'all' | 'playground' | 'api' | 'successful' | 'failed';
type TimeRange = '24h' | '7d' | '30d' | 'all';

const TwoPanel = styled.div<{ $showDetail: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  min-height: 600px;
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  background: var(--bg-surface);

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
    min-height: 0;

    [data-panel='list'] {
      display: ${({ $showDetail }) => ($showDetail ? 'none' : 'flex')};
    }

    [data-panel='detail'] {
      display: ${({ $showDetail }) => ($showDetail ? 'flex' : 'none')};
    }
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 320px;

  @media (max-width: 1023px) {
    min-height: 280px;
  }
`;

const PanelLeft = styled(Panel)`
  border-right: 1px solid var(--border);

  @media (max-width: 1023px) {
    border-right: none;
    border-bottom: none;
  }
`;

const BackToList = styled.button`
  display: none;
  align-items: center;
  gap: 0.25rem;
  border: none;
  background: none;
  color: var(--accent);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
  margin-right: 0.5rem;

  @media (max-width: 1023px) {
    display: inline-flex;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.875rem 1.25rem;
  font-size: 0.8125rem;
  font-weight: 600;
  border-bottom: 1px solid var(--border);
  color: var(--text-primary);
  background: var(--bg-elevated, var(--bg-surface));
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid var(--border);
`;

const DateSelect = styled.select`
  font-size: 0.8125rem;
  padding: 0.375rem 0.5rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--bg-surface);
  color: var(--text-primary);
`;

const PanelScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
`;

const SessionItem = styled.button<{ $selected?: boolean; $failed?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  width: 100%;
  text-align: left;
  padding: 0.875rem 1.25rem;
  border: none;
  border-left: 3px solid
    ${({ $selected, $failed }) =>
      $selected ? 'var(--accent)' : $failed ? 'var(--error, #dc2626)' : 'transparent'};
  background: ${({ $selected }) =>
    $selected ? 'var(--bg-elevated, rgba(26, 122, 112, 0.06))' : 'transparent'};
  cursor: pointer;
  color: inherit;

  &:hover {
    background: var(--bg-elevated, rgba(15, 23, 42, 0.04));
  }
`;

const SessionBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const SessionSummary = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  font-size: 0.75rem;
  color: var(--text-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-top: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const DetailHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const DetailTitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const StaffNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: var(--text-muted);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: var(--bg-elevated, rgba(15, 23, 42, 0.04));
`;

const GapNote = styled.p`
  margin: 0.5rem 0 0;
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const StatsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin: 1rem 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;

  strong {
    color: var(--text-primary);
    font-size: 0.9375rem;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const PreviewPad = styled.div`
  padding: 1.25rem;
  overflow-y: auto;
  flex: 1;
`;

function statusDotStatus(status: SessionStatus): 'active' | 'error' | 'draft' {
  if (status === 'failed') return 'error';
  if (status === 'success') return 'active';
  return 'draft';
}

const SessionsTab: React.FC<SessionsTabProps> = ({
  agentId,
  agentName,
  onNewSession,
  onOpenInPlayground,
}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { show } = toast;

  const [filter, setFilter] = useState<FilterId>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>('7d');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [mobileShowDetail, setMobileShowDetail] = useState(false);

  const { data, isLoading, isError } = useGetSessionsQuery({ agentName });
  const sessions = data?.sessions ?? [];

  const filtered = useMemo(() => {
    return sessions
      .filter((s) => withinTimeRange(s.updatedAt || s.createdAt, timeRange))
      .filter((s) => {
        const source = deriveSource(s.metadata);
        const status = deriveStatus(s.metadata);
        if (filter === 'playground') return source === 'playground';
        if (filter === 'api') return source === 'api';
        if (filter === 'successful') return status === 'success';
        if (filter === 'failed') return status === 'failed';
        return true;
      })
      .sort((a, b) => {
        const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
        const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
        return tb - ta;
      });
  }, [sessions, filter, timeRange]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filter, timeRange, agentName]);

  useEffect(() => {
    if (!pageItems.length) {
      setSelectedId(null);
      return;
    }
    if (!selectedId || !pageItems.some((s) => s.sessionId === selectedId)) {
      const failed = pageItems.find((s) => deriveStatus(s.metadata) === 'failed');
      setSelectedId((failed || pageItems[0]).sessionId);
    }
  }, [pageItems, selectedId]);

  const selected = sessions.find((s) => s.sessionId === selectedId) || null;

  const { data: history, isLoading: historyLoading } = useGetSessionHistoryQuery(
    selectedId || '',
    { skip: !selectedId },
  );

  const { data: analyticsSpot } = useGetAnalyticsSessionQuery(selectedId || '', {
    skip: !selectedId,
  });

  const [cloneSession, { isLoading: cloning }] = useCloneSessionMutation();

  const failedCount = sessions.filter((s) => deriveStatus(s.metadata) === 'failed').length;
  const sourceUnknown = sessions.some((s) => deriveSource(s.metadata) === 'unknown');

  const chipItems: FilterChipItem[] = [
    { id: 'all', label: 'All', count: sessions.length },
    { id: 'playground', label: 'Playground' },
    { id: 'api', label: 'API' },
    { id: 'successful', label: 'Successful' },
    { id: 'failed', label: 'Failed', count: failedCount || undefined },
  ];

  const previewEntries: ConversationEntry[] = useMemo(() => {
    const msgs = history?.messages ?? [];
    const truncated = msgs.slice(-6);
    return truncated.map((m) => ({
      type: 'message' as const,
      data: {
        role: m.role === 'assistant' ? 'Agent' : m.role === 'user' ? 'User' : m.role,
        content: m.content,
      },
    }));
  }, [history?.messages]);

  const handleClone = async (session: SessionListItem) => {
    try {
      const result = await cloneSession(session.sessionId).unwrap();
      show?.({ message: 'Session cloned into Playground', type: 'success' });
      onOpenInPlayground(result.sessionId);
    } catch {
      show?.({ message: 'Failed to clone session', type: 'error' });
    }
  };

  const emptyFailedFilter =
    filter === 'failed' && filtered.length === 0 && sessions.length > 0;

  return (
    <TwoPanel
      data-testid="SessionsTab"
      data-mobile-panel={mobileShowDetail ? 'detail' : 'list'}
      $showDetail={mobileShowDetail}
    >
      <PanelLeft data-panel="list">
        <PanelHeader>
          <span>Sessions</span>
          <Button variant="primary" size="sm" onClick={onNewSession}>
            + New Session
          </Button>
        </PanelHeader>
        <Toolbar>
          <FilterChips
            items={chipItems}
            activeId={filter}
            onChange={(id) => setFilter(id as FilterId)}
          />
          <DateSelect
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as TimeRange)}
            aria-label="Time range"
          >
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="all">All time</option>
          </DateSelect>
        </Toolbar>
        <PanelScroll>
          {isLoading && (
            <Text variant="body1" style={{ padding: '1rem 1.25rem', color: 'var(--text-muted)' }}>
              Loading sessions…
            </Text>
          )}
          {isError && (
            <EmptyState
              title="Unable to load sessions"
              description="Check that you are signed in with an organisation."
            />
          )}
          {!isLoading && !isError && emptyFailedFilter && (
            <EmptyState
              title="No failed sessions"
              description="Failure status is not persisted yet for most sessions. Showing an empty Failed filter rather than inventing status."
            />
          )}
          {!isLoading && !isError && !emptyFailedFilter && filtered.length === 0 && (
            <EmptyState
              title="No sessions yet"
              description="Start a Playground chat or call the Direct API for this agent."
              action={
                <Button variant="primary" size="sm" onClick={onNewSession}>
                  New Session
                </Button>
              }
            />
          )}
          {pageItems.map((session) => {
            const source = deriveSource(session.metadata);
            const status = deriveStatus(session.metadata);
            const failed = status === 'failed';
            return (
              <SessionItem
                key={session.sessionId}
                type="button"
                $selected={session.sessionId === selectedId}
                $failed={failed}
                onClick={() => {
                  setSelectedId(session.sessionId);
                  setMobileShowDetail(true);
                }}
              >
                <StatusDot status={statusDotStatus(status)} size="sm" />
                <SessionBody>
                  <SessionSummary>
                    {provisionalSummary(
                      session,
                      session.sessionId === selectedId ? history?.messages : undefined,
                    )}
                  </SessionSummary>
                  <SessionMeta>
                    <span>{formatSessionId(session.sessionId)}</span>
                    <Badge variant={source === 'api' ? 'warning' : 'info'} size="sm">
                      {sourceLabel(source)}
                    </Badge>
                    <span>
                      {approxDuration(session.createdAt, session.updatedAt)}
                    </span>
                    <span>{relativeTime(session.updatedAt || session.createdAt)}</span>
                  </SessionMeta>
                </SessionBody>
                <Badge
                  variant={
                    status === 'failed'
                      ? 'error'
                      : status === 'success'
                        ? 'success'
                        : 'neutral'
                  }
                  size="sm"
                >
                  {statusLabel(status)}
                </Badge>
              </SessionItem>
            );
          })}
        </PanelScroll>
        {filtered.length > PAGE_SIZE && (
          <PaginationRow>
            <Button
              variant="ghost"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <span>
              {page} / {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </PaginationRow>
        )}
        {sourceUnknown && (
          <GapNote style={{ padding: '0 1.25rem 0.75rem' }}>
            Source badge shows Unknown when session metadata lacks playground/API
            tagging (older sessions before source was preserved).
          </GapNote>
        )}
      </PanelLeft>

      <Panel data-panel="detail">
        <PanelHeader>
          <BackToList
            type="button"
            onClick={() => setMobileShowDetail(false)}
            aria-label="Back to session list"
          >
            ← Sessions
          </BackToList>
          <span>Session detail</span>
        </PanelHeader>
        <PreviewPad>
          {!selected && (
            <EmptyState
              title="Select a session"
              description="Choose a session from the list to preview the conversation trail."
            />
          )}
          {selected && (
            <>
              <DetailHeader>
                <DetailTitleRow>
                  <StatusDot
                    status={statusDotStatus(deriveStatus(selected.metadata))}
                    size="sm"
                  />
                  <Text variant="body1" style={{ fontWeight: 600, margin: 0 }}>
                    {provisionalSummary(selected, history?.messages)}
                  </Text>
                  <Badge
                    variant={
                      deriveSource(selected.metadata) === 'api' ? 'warning' : 'info'
                    }
                    size="sm"
                  >
                    {sourceLabel(deriveSource(selected.metadata))}
                  </Badge>
                  <Badge
                    variant={
                      deriveStatus(selected.metadata) === 'failed'
                        ? 'error'
                        : deriveStatus(selected.metadata) === 'success'
                          ? 'success'
                          : 'neutral'
                    }
                    size="sm"
                  >
                    {statusLabel(deriveStatus(selected.metadata))}
                  </Badge>
                </DetailTitleRow>
                <SessionMeta>
                  <span>{selected.sessionId}</span>
                  <span>{relativeTime(selected.updatedAt || selected.createdAt)}</span>
                </SessionMeta>
                <StaffNote>
                  Full detail · staff — role-aware redaction not enforced yet.
                </StaffNote>
              </DetailHeader>

              <StatsRow>
                <Stat>
                  <span>Duration</span>
                  <strong>
                    {approxDuration(selected.createdAt, selected.updatedAt)}
                  </strong>
                </Stat>
                <Stat>
                  <span>Messages</span>
                  <strong>{history?.messages?.length ?? '—'}</strong>
                </Stat>
                <Stat>
                  <span>Tokens</span>
                  <strong>
                    {analyticsSpot?.tokensTotal != null
                      ? analyticsSpot.tokensTotal.toLocaleString()
                      : '—'}
                  </strong>
                </Stat>
              </StatsRow>

              {historyLoading && (
                <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                  Loading conversation…
                </Text>
              )}
              {!historyLoading && previewEntries.length > 0 && (
                <ConversationPreview
                  title="Conversation preview"
                  entries={previewEntries}
                />
              )}
              {!historyLoading && previewEntries.length === 0 && (
                <EmptyState
                  title="No messages"
                  description="This session has no stored conversation history yet."
                />
              )}
              {(history?.messages?.length ?? 0) > 6 && (
                <GapNote>
                  Preview truncated — open full replay for the continuous trail.
                </GapNote>
              )}

              <Actions>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    navigate(`/agents/${agentId}/sessions/${selected.sessionId}`)
                  }
                >
                  Open Full →
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={cloning}
                  onClick={() => handleClone(selected)}
                >
                  {cloning ? 'Cloning…' : 'Clone session'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onOpenInPlayground(selected.sessionId)}
                >
                  Open in Playground
                </Button>
              </Actions>
            </>
          )}
        </PreviewPad>
      </Panel>
    </TwoPanel>
  );
};

export default SessionsTab;
